/**
 * Builds the document an HTML artifact runs inside.
 *
 * The application owns this shell; the model owns only the fragment - and the
 * fragment runs one level down, inside a frame this shell embeds. That nesting
 * is what makes the policy enforceable. A document's own CSP cannot say where
 * that document navigates; it can only say where the frames it embeds may go.
 * So with the model's markup in a child frame, `frame-src 'none'` below is the
 * leash on it: a script that assigns `location` is navigating a nested context,
 * and this policy blocks it. Sandboxing the child separately denies it the
 * ancestors - a sandboxed context may navigate itself and its descendants, and
 * the top only with allow-top-navigation - so it cannot redirect this shell
 * either. Both halves are needed; neither alone closes the channel.
 *
 * The CSP meta tag is parsed before any model content, and because CSP policies
 * compose restrictively - the effective policy is the intersection of all of
 * them - a policy the fragment supplies can only narrow ours, never widen it.
 *
 * This file is the single place the artifact runtime's policy is expressed.
 */

const CSP_DIRECTIVES = [
    "default-src 'none'",
    "style-src 'unsafe-inline'",
    "script-src 'unsafe-inline'",
    'img-src data:',
    "form-action 'none'",
    "base-uri 'none'",
    "frame-src 'none'",
];

const FRAGMENT_SANDBOX = 'allow-scripts';
const SHELL_STYLE = 'html,body{margin:0;height:100%}iframe{display:block;width:100%;height:100%;border:0}';
const FRAGMENT_STYLE = '*{box-sizing:border-box}html,body{margin:0}';
const escapeForSrcdocAttribute = markup =>
    String(markup)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;');
const buildFragmentDocument = fragment => `<style>${FRAGMENT_STYLE}</style>${fragment || ''}`;

/**
 * @param {string} fragment - model-generated HTML body fragment, untrusted.
 * @returns {string} a complete document for the frame's srcDoc.
 */
export const buildArtifactDocument = fragment => `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CSP_DIRECTIVES.join('; ')}">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${SHELL_STYLE}</style>
</head>
<body>
<iframe sandbox="${FRAGMENT_SANDBOX}" srcdoc="${escapeForSrcdocAttribute(buildFragmentDocument(fragment))}"></iframe>
</body>
</html>`;

export const ARTIFACT_CSP = CSP_DIRECTIVES.join('; ');
